import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { forEach, get, isUndefined } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { ResponseChannel } from 'common/response-editor/menu-bar/MenuBar.types';

import 'common/response-editor/menu-bar/MenuBar.scss';

export const ResponseChannelsInfo: any = {
    [ResponseChannel.Email]: {
        id: ResponseChannel.Email,
        label: 'EMAIL',
        hideRichText: false,
    },
    [ResponseChannel.SMS]: {
        id: ResponseChannel.SMS,
        label: 'SMS',
        hideRichText: true,
    },
    [ResponseChannel.Respond]: {
        id: ResponseChannel.Respond,
        label: 'RESPOND',
        hideRichText: false,
    },
    [ResponseChannel.InternalNote]: {
        id: ResponseChannel.InternalNote,
        label: 'INTERNAL_NOTE',
        hideRichText: false,
    },
    [ResponseChannel.DirectMessage]: {
        id: ResponseChannel.DirectMessage,
        label: 'DIRECT_MESSAGE',
        hideRichText: false,
    },
};

export interface ResponseChannelsData {
    id: ResponseChannel;
    backgroundColor?: string;
}

interface PrivateOptions {
    enable: boolean;
    label?: string;
    tooltip?: string;
    checked?: boolean;
    backgroundColor?: string;
    locked?: boolean;
}

interface MenuBarHandlers {
    onChannelSelection?: any;
    onPrivateCheckboxChange?: any;
}

export interface Props {
    enable: boolean;
    privateOptions?: PrivateOptions;
    activeResponseChannelIndex?: number;
    responseChannelsData?: ResponseChannelsData[];
    handlers?: MenuBarHandlers;
}

const ResponseChannels = ({
    data,
    activeChannelId,
    selectResponseChannel,
    t,
}: {
    data: ResponseChannelsData[];
    activeChannelId: number;
    selectResponseChannel: any;
    t: any;
}): JSX.Element => {
    const channels: JSX.Element[] = [];

    forEach(data, (channelItem, channelKey) => {
        channels.push(
            <span
                key={channelKey}
                role="button"
                tabIndex={0}
                className={`response-channels-item ${activeChannelId === channelKey ? 'active' : ''}`}
                onClick={(): void => selectResponseChannel(channelKey)}
                onKeyDown={(): void => {
                    // do nothing
                }}
            >
                {t(ResponseChannelsInfo[channelItem.id].label)}
            </span>,
        );
    });
    return <article className="menu-bar-response-channels">{channels}</article>;
};

const PrivateCheckBox = ({
    isPrivate,
    label,
    handleCheckboxChange,
    tooltip,
    locked,
}: {
    isPrivate: boolean;
    label: string;
    handleCheckboxChange: any;
    tooltip: string;
    locked: boolean;
}): JSX.Element => {
    const LabelCheckbox = useMemo(
        () => (
            <label htmlFor="menu-private" className="private-label-container">
                <input
                    disabled={locked}
                    id="menu-private"
                    type="checkbox"
                    checked={isPrivate}
                    onChange={handleCheckboxChange}
                />
                <span className="private-label">{label}</span>
            </label>
        ),
        [handleCheckboxChange, isPrivate, label, locked],
    );

    return (
        <article className="menu-bar-private">
            {tooltip ? (
                <OverlayTrigger overlay={<Tooltip id="private-tooltip">{tooltip}</Tooltip>}>
                    {LabelCheckbox}
                </OverlayTrigger>
            ) : (
                LabelCheckbox
            )}
        </article>
    );
};

export const MenuBar = ({
    enable,
    activeResponseChannelIndex,
    responseChannelsData,
    privateOptions,
    handlers,
}: Props): JSX.Element | null => {
    const { t } = useTranslation(['common/response-editor']);
    const [activeIndex, setActiveIndex] = useState<number>(activeResponseChannelIndex || 0);
    const [isPrivate, setIsPrivate] = useState(get(privateOptions, `checked`, false));

    const selectResponseChannel = useCallback(
        (index: number): void => {
            setActiveIndex(index);
            if (handlers!.onChannelSelection) {
                handlers!.onChannelSelection(index);
            }
        },
        [handlers],
    );

    useEffect(() => {
        if (!isUndefined(activeResponseChannelIndex) && activeIndex !== activeResponseChannelIndex) {
            selectResponseChannel(activeResponseChannelIndex!);
        }
    }, [activeResponseChannelIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newValue = event.target.checked;

        setIsPrivate(newValue);
        if (handlers!.onPrivateCheckboxChange) {
            handlers!.onPrivateCheckboxChange(newValue);
        }
    };

    if (enable) {
        return (
            <section className="r4e-response-editor-menu-bar">
                <ResponseChannels
                    data={responseChannelsData!}
                    activeChannelId={activeIndex}
                    selectResponseChannel={selectResponseChannel}
                    t={t}
                />
                {privateOptions!.enable ? (
                    <PrivateCheckBox
                        isPrivate={isPrivate}
                        label={t(privateOptions!.label!)}
                        tooltip={privateOptions!.tooltip!}
                        locked={privateOptions!.locked!}
                        handleCheckboxChange={handleCheckboxChange}
                    />
                ) : null}
            </section>
        );
    }
    return null;
};
